"use client"
import { SearchBar } from '@/components/ui';
import * as Avatar from '@radix-ui/react-avatar';
import * as Select from '@radix-ui/react-select';
import { Badge, Button, Table } from '@radix-ui/themes';
import { fetchJson, HttpError } from '@/lib/fetch-json';
import {
  AlertCircle,
  Check,
  ChevronDown,
  Filter,
  Pencil,
  Plus,
  RefreshCw,
  Trash2
} from 'lucide-react';
import { useSession } from 'next-auth/react';
import React, { useEffect, useState } from 'react';
import { AdminOnly, useRBAC } from './RBACGuard';
import UserCreateModal from './UserCreateModal';
import UserDeleteModal from './UserDeleteModal';

interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  lastLogin: string | null;
  avatar: string;
  createdAt: string;
}

type UserRole = 'Admin' | 'Editor' | 'Author';
type UserStatus = 'Active' | 'Inactive';
type FilterOption = 'All' | UserRole | UserStatus;

const roles: readonly FilterOption[] = ['All', 'Editor', 'Author'];
const statuses: readonly FilterOption[] = ['All', 'Active', 'Inactive'];

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  users?: User[];
}


export default function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<FilterOption>('All');
  const [selectedStatus, setSelectedStatus] = useState<FilterOption>('All');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const { isAdmin, role: userRole } = useRBAC();
  const { data: session } = useSession();

  // Handle filter changes with type safety
  const handleRoleFilterChange = (value: string) => {
    setSelectedRole(value as FilterOption);
  };

  const handleStatusFilterChange = (value: string) => {
    setSelectedStatus(value as FilterOption);
  };

  // Memoize filtered users to prevent unnecessary re-computations
  const filteredUsers = React.useMemo(() => {
    // Early return if no users
    if (!users.length) return [];

    // Optimize search term processing
    const searchTermLower = searchTerm.toLowerCase().trim();

    return users.filter(user => {
      // Fast path for "All" filters
      if (selectedRole === 'All' && selectedStatus === 'All') {
        if (!searchTermLower) return true;
        return user.name.toLowerCase().includes(searchTermLower) ||
          user.email.toLowerCase().includes(searchTermLower);
      }

      // Check role filter
      if (selectedRole !== 'All' && user.role !== selectedRole) return false;

      // Check status filter
      if (selectedStatus !== 'All' && user.status !== selectedStatus) return false;

      // Check search term
      if (searchTermLower) {
        return user.name.toLowerCase().includes(searchTermLower) ||
          user.email.toLowerCase().includes(searchTermLower);
      }

      return true;
    });
  }, [users, searchTerm, selectedRole, selectedStatus]);


  // Handle refresh button click
  const handleRefresh = () => {
    fetchUsers(true);
  };

  // Fetch users from API with improved error handling
  const fetchUsers = async (showLoading = true): Promise<void> => {
    if (showLoading) {
      setLoading(true);
    }
    setError('');

    try {
      const data: ApiResponse<User[]> = await fetchJson<ApiResponse<User[]>>('/api/admin/users', {
        timeoutMs: 10000,
      });

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch users');
      }

      if (!Array.isArray(data.users)) {
        console.warn('API returned invalid users data:', data);
        setUsers([]);
        setError('Received invalid data format from server');
        return;
      }

      setUsers(data.users);
    } catch (err) {
      console.error('Error fetching users:', err);

      let errorMessage = 'Failed to load users';
      if (err instanceof HttpError) {
        const errorData = err.body as { error?: string; message?: string } | undefined;
        errorMessage =
          errorData?.error ||
          errorData?.message ||
          err.message ||
          `Server error: ${err.status}`;
      } else if (err instanceof Error) {
        if (err.message.includes('timed out') || err.message.includes('timeout')) {
          errorMessage = 'Request timed out. Please check your connection.';
        } else {
          errorMessage = err.message;
        }
      }

      setError(errorMessage);
      setUsers([]); // Clear users on error
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  // Load users on component mount - simplified to prevent infinite loops
  useEffect(() => {
    // Only run if userRole is defined and user is admin
    if (userRole !== 'Admin') {
      setLoading(false);
      return;
    }

    let isMounted = true;

    const loadData = async () => {
      if (isMounted) {
        try {
          await fetchUsers();
        } catch (error) {
          // Error is already handled in fetchUsers
          console.error('Failed to load users on mount:', error);
        }
      }
    };

    loadData();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [userRole]); // Only depend on userRole to prevent infinite loops

  // Cleanup effect for modal states
  useEffect(() => {
    return () => {
      // Reset all modal states and clear any pending operations
      setIsCreateModalOpen(false);
      setIsDeleteModalOpen(false);
      setSelectedUser(null);
      setEditingUser(null);
      setError('');
    };
  }, []);

  // Handle user creation
  const handleUserCreated = async () => {
    try {
      // Refresh the user list silently (without loading state)
      await fetchUsers(false);
      // Reset edit mode
      setEditingUser(null);
    } catch (error) {
      console.error('Failed to refresh users after creation:', error);
      // Still reset edit mode even if refresh fails
      setEditingUser(null);
    }
  };

  // Handle user edit with validation
  const handleEditUser = (user: User) => {
    if (!user || !user.id) {
      console.error('Invalid user provided to handleEditUser:', user);
      setError('Cannot edit user: Invalid user data');
      return;
    }

    setEditingUser(user);
    setIsCreateModalOpen(true);
    // Clear any existing errors
    setError('');
  };

  // Handle user delete with improved error handling
  const handleDeleteUser = async (userId: string): Promise<void> => {
    if (!userId || typeof userId !== 'string') {
      throw new Error('Invalid user ID provided');
    }

    try {
      const data: ApiResponse = await fetchJson<ApiResponse>(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        timeoutMs: 8000,
      });

      if (data.success === false) {
        throw new Error(data.error || 'Failed to delete user');
      }

      // Success - refresh the list silently (without loading state)
      await fetchUsers(false);
      setIsDeleteModalOpen(false);
      setSelectedUser(null);
    } catch (err) {
      console.error('Error deleting user:', err);

      let errorMessage = 'Failed to delete user';
      if (err instanceof HttpError) {
        const errorData = err.body as { error?: string; message?: string } | undefined;
        errorMessage =
          errorData?.error ||
          errorData?.message ||
          err.message ||
          `Server error: ${err.status}`;
        if (err.status === 404) {
          errorMessage = 'User not found. It may have already been deleted.';
        } else if (err.status === 403) {
          errorMessage = 'You do not have permission to delete this user.';
        } else if (err.status === 409) {
          errorMessage = 'Cannot delete user due to existing dependencies.';
        }
      } else if (err instanceof Error) {
        if (err.message.includes('timed out') || err.message.includes('timeout')) {
          errorMessage = 'Request timed out. Please try again.';
        } else {
          errorMessage = err.message;
        }
      }

      throw new Error(errorMessage);
    }
  };

  // Open delete confirmation modal with validation
  const openDeleteModal = (user: User) => {
    if (!user || !user.id || !user.name) {
      console.error('Invalid user provided to openDeleteModal:', user);
      setError('Cannot delete user: Invalid user data');
      return;
    }

    // Prevent admin from deleting themselves (button is already disabled, but extra safety check)
    if (session?.user?.email === user.email) {
      return; // Silently prevent modal opening for current user
    }

    setSelectedUser(user);
    setIsDeleteModalOpen(true);
    // Clear any existing errors
    setError('');
  };

  // Handle modal close with cleanup
  const handleCreateModalClose = () => {
    setIsCreateModalOpen(false);
    setEditingUser(null);
    setError('');
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
    setSelectedUser(null);
    setError('');
  };

  // Show access denied for non-admin users
  if (!isAdmin()) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-600 mb-4">
            Only administrators can manage users. Please contact your administrator if you need access.
          </p>
          <p className="text-sm text-gray-500">
            Current role: {userRole || 'Not logged in'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <AdminOnly>
      <div>
        {/* Error Alert */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
                <button
                  onClick={handleRefresh}
                  className="mt-2 text-sm text-red-600 hover:text-red-500 font-medium"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200" role="main" aria-label="User management interface">
          {/* Header with Search and Filters */}
          <div className="px-6 py-4 border-b border-gray-200" role="toolbar" aria-label="User filters and search">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 space-x-3">
              <div className="flex-1 max-w-md">
                <SearchBar
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Search users..."
                  aria-label="Search users by name or email"
                />
              </div>
              <div className="flex items-center gap-4">
                <Button
                  onClick={() => setIsCreateModalOpen(true)}
                  aria-label="Create new user account"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Create User
                </Button>

                {/* Refresh Button */}
                <Button
                  variant="outline"
                  onClick={handleRefresh}
                  disabled={loading}
                  title="Refresh user list"
                  aria-label="Refresh user list"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
                    aria-hidden="true"
                  />
                </Button>

                {/* Role Filter */}
                <div className="flex items-center gap-3">
                  <Filter className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  <Select.Root value={selectedRole} onValueChange={handleRoleFilterChange}>
                    <Select.Trigger
                      className="flex items-center justify-between w-32 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                      aria-label="Filter users by role"
                    >
                      <Select.Value />
                      <Select.Icon aria-hidden="true">
                        <ChevronDown className="h-4 w-4" />
                      </Select.Icon>
                    </Select.Trigger>
                    <Select.Portal>
                      <Select.Content className="bg-white border border-gray-200 rounded-md shadow-lg z-10">
                        <Select.Viewport className="p-1">
                          {roles.map(role => (
                            <Select.Item
                              key={role}
                              value={role}
                              className="relative flex items-center px-8 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded cursor-pointer focus:bg-gray-100 focus:outline-none"
                            >
                              <Select.ItemText>{role}</Select.ItemText>
                              <Select.ItemIndicator className="absolute left-2">
                                <Check className="h-4 w-4" />
                              </Select.ItemIndicator>
                            </Select.Item>
                          ))}
                        </Select.Viewport>
                      </Select.Content>
                    </Select.Portal>
                  </Select.Root>
                </div>

                {/* Status Filter */}
                <div className="flex items-center space-x-2">
                  <Filter className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  <Select.Root value={selectedStatus} onValueChange={handleStatusFilterChange}>
                    <Select.Trigger
                      className="flex items-center justify-between w-32 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                      aria-label="Filter users by status"
                    >
                      <Select.Value />
                      <Select.Icon aria-hidden="true">
                        <ChevronDown className="h-4 w-4" />
                      </Select.Icon>
                    </Select.Trigger>
                    <Select.Portal>
                      <Select.Content className="bg-white border border-gray-200 rounded-md shadow-lg z-10">
                        <Select.Viewport className="p-1">
                          {statuses.map(status => (
                            <Select.Item
                              key={status}
                              value={status}
                              className="relative flex items-center px-8 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded cursor-pointer focus:bg-gray-100 focus:outline-none"
                            >
                              <Select.ItemText>{status}</Select.ItemText>
                              <Select.ItemIndicator className="absolute left-2">
                                <Check className="h-4 w-4" />
                              </Select.ItemIndicator>
                            </Select.Item>
                          ))}
                        </Select.Viewport>
                      </Select.Content>
                    </Select.Portal>
                  </Select.Root>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <RefreshCw className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
              <p className="text-gray-600">Loading users...</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
            <p className="text-gray-600">
              {users.length === 0 ? 'No users have been created yet.' : 'Try adjusting your search or filters.'}
            </p>
          </div>
        )}

        {/* Users Table */}
        {!loading && filteredUsers.length > 0 && (
          <div className="overflow-x-auto mt-5">
            <Table.Root
              aria-label={`Users table showing ${filteredUsers.length} of ${users.length} users`}
            >
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell aria-sort="none">User</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell aria-sort="none">Role</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell aria-sort="none">Status</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell aria-sort="none">Last Login</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell aria-sort="none">Actions</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredUsers.map((user) => (
                  <Table.Row
                    key={user.id}
                    className={session?.user?.email === user.email ? "bg-blue-50 border-blue-200" : ""}
                  >
                    <Table.Cell>
                      <div className="flex items-center">
                        <Avatar.Root className="h-10 w-10 rounded-full bg-primary text-white flex items-center justify-center text-sm font-medium">
                          <Avatar.Fallback>{user.avatar}</Avatar.Fallback>
                        </Avatar.Root>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge
                        variant="soft"
                        color={
                          user.role === 'Admin' ? 'red' :
                            user.role === 'Editor' ? 'blue' :
                              user.role === 'Author' ? 'green' :
                                'gray'
                        }
                      >
                        {user.role}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge variant="soft" color={user.status === 'Active' ? 'green' : 'gray'}>
                        {user.status}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-sm text-gray-500">
                        {user.lastLogin || 'Never'}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex items-center gap-3" role="group" aria-label={`Actions for ${user.name}`}>
                        <Button
                          variant="ghost"
                          size="1"
                          color="blue"
                          title="Edit user"
                          onClick={() => handleEditUser(user)}
                          aria-label={`Edit user ${user.name}`}
                        >
                          <Pencil className="h-4 w-4" aria-hidden="true" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="1"
                          color="red"
                          title={session?.user?.email === user.email ? "Cannot delete your own account" : "Delete user"}
                          onClick={() => openDeleteModal(user)}
                          disabled={session?.user?.email === user.email}
                          aria-label={session?.user?.email === user.email ? "Cannot delete your own account" : `Delete user ${user.name}`}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </div>
        )}

      </div>

      {/* User Creation Modal */}
      <UserCreateModal
        isOpen={isCreateModalOpen}
        onClose={handleCreateModalClose}
        onUserCreated={handleUserCreated}
        editingUser={editingUser}
      />

      {/* User Delete Modal */}
      <UserDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        user={selectedUser}
        onConfirmDelete={handleDeleteUser}
      />
    </AdminOnly>
  );
}
